/**
 * THE stage map. Owner-ratified 29 Jul, locked in `PHASE2-REPORT.md` §2.
 *
 * ===========================================================================
 * ONE MAP, AND NO COMPONENT MAY HARDCODE A LABEL
 * ===========================================================================
 * The `inquiries.stage` ENUM is authoritative and unchanged; everything in this
 * file is presentation. The rule from §2 is literal: "Display strings live in
 * ONE map in `apps/web`; no component hardcodes a label." If you find yourself
 * typing "Advance Pending" anywhere else in this app, that is the bug.
 *
 * THE RATIFIED MAPPING that this file exists to record: Figma's tab, column and
 * pill all read **"Advance Pending"**, and that is a DISPLAY STRING for the enum
 * member `quotation_confirmed`. It is not a stage, it has no enum value, and no
 * migration follows from it.
 *
 * `follow_up` and `won_closed` are real, reachable enum values — the API will
 * return them and the Record page must render them — but the design gives them
 * no tab, no column and no `stage/*` colour pair. They therefore get
 * `board: false` and `tone: 'neutral'`. Nothing is invented to paint them: a
 * neutral pill is an honest "the design does not colour this", where a
 * hand-picked tint would be a silent invention of a token.
 *
 * SOURCES, per field:
 *  · `label`      — Master plan §3 rule 10 as transcribed in PHASE2-REPORT §2.
 *  · `tabLabel`   — Figma `StageTabs` (node I12:873;6:447): All / New / Need
 *                   Quote / Pending Confirmation / Advance Pending / Lost.
 *  · `pillLabel`  — Figma component set `Pill/Stage` (node 6:34), 9 variants.
 *                   NOTE `new` genuinely has two strings: the tab says "New",
 *                   the pill says "New Inquiry". Both are in the design; both
 *                   are kept.
 *  · `nextStep`   — Figma `Widget/InquiriesNeedingAction` (12:3101) NEXT STEP
 *                   column. `null` where the design states none.
 *  · `tone`       — which `stage/*` token pair from "CRF Tokens v3" backs it.
 */

/**
 * The `stage` ENUM, in DDL order (`apps/api/src/entities/inquiry.entity.ts:26-34`).
 * Declared here rather than imported at runtime so the browser bundle carries no
 * dependency on the CommonJS `@crf/shared` build; `stages.test.ts` asserts this
 * array is identical to the API's.
 */
export const INQUIRY_STAGES = [
  'new',
  'follow_up',
  'need_quote',
  'pending_confirmation',
  'quotation_confirmed',
  'won_closed',
  'lost',
] as const;

export type InquiryStage = (typeof INQUIRY_STAGES)[number];

/** Which `stage/*` token pair paints a pill. `neutral` = the design colours none. */
export type StageTone = 'new' | 'quote' | 'confirm' | 'advance' | 'lost' | 'neutral';

export interface StageDescriptor {
  readonly stage: InquiryStage;
  /** Canonical UI label (Master plan §3 r10). Always present. */
  readonly label: string;
  /** Figma `StageTabs` label. `null` = no tab and no board column. */
  readonly tabLabel: string | null;
  /** Figma `Pill/Stage` variant label. `null` = the design has no pill for it. */
  readonly pillLabel: string | null;
  /** Does this stage get a tab and a board column THIS PHASE? (§2: five do.) */
  readonly board: boolean;
  readonly tone: StageTone;
  /** Figma NEXT STEP copy. `null` where the design states none. */
  readonly nextStep: string | null;
}

export const STAGES: Readonly<Record<InquiryStage, StageDescriptor>> = {
  new: {
    stage: 'new',
    label: 'New',
    tabLabel: 'New',
    pillLabel: 'New Inquiry',
    board: true,
    tone: 'new',
    nextStep: 'First call',
  },
  follow_up: {
    stage: 'follow_up',
    label: 'Follow Up',
    // No tab, no column, no `stage/*` token — §2. Reachable by API and Record only.
    tabLabel: null,
    pillLabel: null,
    board: false,
    tone: 'neutral',
    nextStep: null,
  },
  need_quote: {
    stage: 'need_quote',
    label: 'Need Quote',
    tabLabel: 'Need Quote',
    pillLabel: 'Need Quote',
    board: true,
    tone: 'quote',
    nextStep: 'Send quote',
  },
  pending_confirmation: {
    stage: 'pending_confirmation',
    label: 'Pending Confirmation',
    tabLabel: 'Pending Confirmation',
    pillLabel: 'Pending Confirmation',
    board: true,
    tone: 'confirm',
    nextStep: 'Follow up',
  },
  quotation_confirmed: {
    stage: 'quotation_confirmed',
    label: 'Quotation Confirmed',
    // THE RATIFIED MAPPING — Figma's label for this enum member, not a new stage.
    tabLabel: 'Advance Pending',
    pillLabel: 'Advance Pending',
    board: true,
    tone: 'advance',
    nextStep: 'Collect advance',
  },
  won_closed: {
    stage: 'won_closed',
    label: 'Won & Closed',
    tabLabel: null,
    pillLabel: null,
    board: false,
    tone: 'neutral',
    nextStep: null,
  },
  lost: {
    stage: 'lost',
    label: 'Lost',
    tabLabel: 'Lost',
    pillLabel: 'Lost',
    board: true,
    tone: 'lost',
    // A lost inquiry has no next action; the design shows none.
    nextStep: null,
  },
};

/**
 * The five stages with a tab and a board column, in Figma's tab order.
 *
 * Derived from `STAGES`, never re-typed: adding a sixth column becomes a
 * one-line change to `board`, and it is impossible for this list and the pill
 * colours to disagree.
 */
export const BOARD_STAGES: readonly InquiryStage[] = INQUIRY_STAGES.filter(
  (stage) => STAGES[stage].board,
);

/**
 * The stages an inquiry is still being worked in — everything except the two
 * terminal ones. Used by "Inquiries needing action": a lost or won inquiry needs
 * nothing.
 *
 * Note this INCLUDES `follow_up`, which has no board column. A pilot user can
 * legitimately hold inquiries in that stage (the API allows the transition), and
 * omitting it here would make those rows invisible on the dashboard as well as on
 * the board — the one place they would still be actionable.
 */
export const OPEN_STAGES: readonly InquiryStage[] = INQUIRY_STAGES.filter(
  (stage) => stage !== 'lost' && stage !== 'won_closed',
);

/** Tailwind class pairs, one per tone. The only place stage colours are named. */
const TONE_CLASSES: Readonly<Record<StageTone, string>> = {
  new: 'bg-stage-new-bg text-stage-new-fg',
  quote: 'bg-stage-quote-bg text-stage-quote-fg',
  confirm: 'bg-stage-confirm-bg text-stage-confirm-fg',
  advance: 'bg-stage-advance-bg text-stage-advance-fg',
  lost: 'bg-stage-lost-bg text-stage-lost-fg',
  // No `stage/*` pair exists for these; bg/subtle + text/secondary + border/default
  // is the design's own neutral chip treatment (Country tile Meta chips, 29:51).
  neutral: 'bg-subtle text-fg-secondary ring-1 ring-line',
};

export function stageToneClasses(tone: StageTone): string {
  return TONE_CLASSES[tone];
}

/** Narrow an unknown string (an API value, a URL query) to a stage. */
export function isInquiryStage(value: unknown): value is InquiryStage {
  return typeof value === 'string' && (INQUIRY_STAGES as readonly string[]).includes(value);
}

/**
 * The label to show on a pill.
 *
 * Falls back to `label` for the two stages the design gives no pill — an honest
 * word in a neutral chip, rather than a blank pill or an invented tint.
 */
export function stagePillLabel(stage: InquiryStage): string {
  const descriptor = STAGES[stage];
  return descriptor.pillLabel ?? descriptor.label;
}

/** The label to show on a tab. Only the five board stages have one. */
export function stageTabLabel(stage: InquiryStage): string {
  const descriptor = STAGES[stage];
  return descriptor.tabLabel ?? descriptor.label;
}

/**
 * Days-in-stage, and whether that count is overdue.
 *
 * ---------------------------------------------------------------------------
 * DOCUMENTED JUDGMENT — THE OVERDUE THRESHOLD IS NOT IN FIGMA
 * ---------------------------------------------------------------------------
 * `Pill/Days` (node 6:78) has exactly two variants, "4d in stage" (neutral) and
 * "12d in stage" (danger tint), and `Widget/InquiriesNeedingAction` renders 2d,
 * 3d, 4d and 6d neutral against 12d in danger. So the design pins the boundary
 * somewhere in **(6, 12]** and states no number.
 *
 * SEVEN is chosen: it is the smallest integer consistent with every instance in
 * the file, and it is one week, which is the unit a coordinator actually thinks
 * in. This is a judgment recorded in PHASE2-REPORT, not a value read off the
 * design — if the owner names a different number, this constant is the only edit.
 */
export const OVERDUE_DAYS_IN_STAGE = 7;

export function daysInStage(stageEnteredAt: string | null, now: Date = new Date()): number | null {
  if (stageEnteredAt === null) return null;
  const entered = new Date(stageEnteredAt);
  const ms = entered.getTime();
  if (!Number.isFinite(ms)) return null;
  const elapsed = now.getTime() - ms;
  if (elapsed < 0) return 0;
  return Math.floor(elapsed / 86_400_000);
}

export function isOverdueInStage(days: number | null): boolean {
  return days !== null && days >= OVERDUE_DAYS_IN_STAGE;
}
